import type { Patient } from "$global-providers/rxdb-provider/collections/patient";
import { useUser } from "$global-providers/user-provider";
import { DataAccessConsentModal } from "$routes/_lib/data-access-consent-modal";
import { PatientOverviewTabOption } from "$routes/_lib/patient-overview-tab-option";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { InvoicingTab as InvoicesTab } from "$routes/patient/[:patientId]/_lib/invoices";
import { PatientCentre } from "$routes/patient/[:patientId]/_lib/patient-centre";
import { AncVisitsTab } from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-visits-tab";
import { useAncVisitsTabVisibility } from "$routes/patient/[:patientId]/antenatal-care/_lib/use-anc-visits-tab-visibility";
import { useElephant200AdditionsFlag } from "$routes/patient/[:patientId]/antenatal-care/_lib/use-elephant-2-00-additions-flag";
import { useOngoingInpatientVisitsQuery } from "$routes/patient/[:patientId]/inpatient/_lib/__generated__/ongoing-inpatient-visits.gql";
import {
  canUserAccessInpatient,
  linkToPatientInpatient,
} from "$routes/patient/[:patientId]/inpatient/links";
import { History as HistoryTab } from "$routes/patient/[:patientId]/overview/history/_lib/history";
import { linkToPatientSearchResults } from "$routes/patient-search/links";
import { linkToWardPatientList } from "$routes/ward-patient-list/links";
import { Tab, Tabs } from "@material-ui/core";
import { Role } from "elephant-web/gql";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

function a11yProps(index: string | number) {
  return {
    id: `patient-details-tab-${index}`,
    "aria-controls": `patient-details-tab-${index}`,
  };
}
function getDefaultTab({
  role,
  invoiceId,
  ancVisitsTabIsDefault,
}: {
  role: Role;
  invoiceId?: string;
  ancVisitsTabIsDefault?: boolean;
}): PatientOverviewTabOption {
  if (invoiceId) {
    return PatientOverviewTabOption.Invoices;
  }

  if ([Role.Admin, Role.Clinic].includes(role)) {
    return PatientOverviewTabOption.Invoices;
  }

  if (role === Role.CashOfficer) {
    return PatientOverviewTabOption.Invoices;
  }

  if (ancVisitsTabIsDefault) {
    return PatientOverviewTabOption.PreviousAncVisits;
  }

  return PatientOverviewTabOption.History;
}

const inpatientReferrers = [
  linkToPatientSearchResults(),
  linkToWardPatientList(),
];

/**
 * Redirect to the inpatient details page
 * if the patient has an inpatient visit,
 * and the feature flag is enabled
 */
function useRedirectToInpatient(patientIsAdmitted: boolean) {
  const location = useLocation();
  const { referrer } = (location.state as { referrer: string }) ?? {};
  const { role } = useUser();

  const canAccess = canUserAccessInpatient(role);

  const cameFromAPageThatMeansWeShouldRedirect =
    inpatientReferrers.includes(referrer);

  return (
    canAccess && cameFromAPageThatMeansWeShouldRedirect && patientIsAdmitted
  );
}

export const PatientOverview: FC<{
  patient: Patient;
  isSideBar?: boolean;
  className?: string;
  /**
   * When provided, the user is currently editing an ANC encounter with this
   * id. The ANC visits tab becomes the default selected tab, and the editing
   * encounter is excluded from the prior-visits list.
   */
  currentAncEncounterId?: string;
}> = ({ patient, isSideBar = false, className, currentAncEncounterId }) => {
  const { role } = useUser();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  const elephant200AdditionsFlagOn = useElephant200AdditionsFlag();
  const ancVisits = useAncVisitsTabVisibility(patient.id);
  const ancVisitsTabShown = elephant200AdditionsFlagOn && ancVisits.shouldShow;
  const ancVisitsTabIsDefault =
    ancVisitsTabShown && currentAncEncounterId != null;

  const { selectedTab, invoiceId } = useQuerystring();
  const availableTabs = new Set<string>([
    PatientOverviewTabOption.History,
    ...(role !== Role.Sonographer ? [PatientOverviewTabOption.Invoices] : []),
    ...(ancVisitsTabShown ? [PatientOverviewTabOption.PreviousAncVisits] : []),
  ]);
  const tab =
    selectedTab && availableTabs.has(selectedTab)
      ? selectedTab
      : getDefaultTab({ role, invoiceId, ancVisitsTabIsDefault });

  const handleTabChange: React.ComponentProps<typeof Tabs>["onChange"] = (
    _,
    newValue,
  ) => {
    const { pathname, search } = location;
    const params = new URLSearchParams(search);
    params.delete("invoiceId");
    params.set("selectedTab", newValue);
    // Any time you set a tab, this ensures the state that came from the last place you were at
    // remains until you leave the page.
    navigate(
      { pathname, search: params.toString() },
      { state: location.state, replace: true },
    );
  };

  // Identify whether a patient is admitted by checking if they have any ongoing inpatient visits
  const { data: inpatientData, loading: inpatientDataLoading } =
    useOngoingInpatientVisitsQuery({
      variables: { input: { patientUuid: patient.id, offset: 0, limit: 20 } },
    });
  const ongoingAdmissions = inpatientData?.ongoingInpatientVisits?.items ?? [];

  const shouldRedirectToInpatient = useRedirectToInpatient(
    ongoingAdmissions.length === 1,
  );

  // TODO: This is so janky - we should figure this out at the point of search.
  if (shouldRedirectToInpatient) {
    return (
      <Navigate
        to={linkToPatientInpatient(patient.id, ongoingAdmissions[0]._id)}
        replace
      />
    );
  }
  return (
    <div className={className}>
      {!isSideBar && <PatientCentre patient={patient} />}
      <Tabs
        value={tab}
        variant="fullWidth"
        onChange={handleTabChange}
        aria-label={formatMessage({
          id: "patientDetails.tabs.ariaLabel",
          defaultMessage: "patient details tab",
        })}
        style={{ width: "100%", marginBottom: "16px" }}
      >
        {ancVisitsTabShown && (
          <Tab
            label={
              <FormattedMessage
                id="patientDetails.tab.previousAncVisits"
                defaultMessage="ANC visits"
              />
            }
            value={PatientOverviewTabOption.PreviousAncVisits}
            {...a11yProps(PatientOverviewTabOption.PreviousAncVisits)}
            data-testid="patientDetailsPreviousAncVisitsTab"
          />
        )}
        <Tab
          label={
            <FormattedMessage
              id="patientDetails.tab.history"
              defaultMessage="History"
            />
          }
          value={PatientOverviewTabOption.History}
          {...a11yProps(PatientOverviewTabOption.History)}
          data-testid="patientDetailsHistoryTab"
        />

        {role !== Role.Sonographer && (
          // TODO WEB-5458 - simplify to a single page w/o tab
          <Tab
            label={
              <FormattedMessage
                id="patientDetails.tab.invoices"
                defaultMessage="Invoices"
              />
            }
            value={PatientOverviewTabOption.Invoices}
            {...a11yProps(PatientOverviewTabOption.Invoices)}
            data-testid="patientDetailsInvoicesTab"
          />
        )}
      </Tabs>
      {tab === PatientOverviewTabOption.PreviousAncVisits &&
      ancVisitsTabShown ? (
        <div data-testid="patientDetailsPreviousAncVisitsTabContent">
          <AncVisitsTab
            encounters={ancVisits.encounters}
            currentEncounterId={currentAncEncounterId}
          />
        </div>
      ) : null}
      {tab === PatientOverviewTabOption.History ? (
        <div data-testid="patientDetailsHistoryTabContent">
          <HistoryTab patient={patient} isSideBar={isSideBar} />
        </div>
      ) : null}
      {tab === PatientOverviewTabOption.Invoices &&
      role !== Role.Sonographer ? (
        <InvoicesTab patientId={patient.id} />
      ) : null}
      <DataAccessConsentModal patientId={patient.id} />
    </div>
  );
};
